import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { getBrandOptions } from 'core/helpers/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { useFormikContext, Form } from 'formik';
import PropTypes from 'prop-types';

import Field from 'core/components/form/form.v2';
import withDatePicker from 'core/components/with-date-picker';
import { useStore } from 'core/store';
import { Collapsible } from '../collapsible';
import { autoDetailsCodes } from '../quote-forms/quote-forms.constants';
import { ErrorAlert } from '../quote-errors';
import useStyles from './auto-details.styles';

// Options for the auto insurance details radio buttons
const autoInsuranceDetailsOptions = [
  {
    value: true,
    label: 'Yes'
  },
  {
    value: false,
    label: 'No'
  }
];

const AutoDetails = ({ sectionsToShow }) => {
  const classes = useStyles();
  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const { state } = values;
  const {
    quote: { getAlerts }
  } = useStore();
  const limitOptions = useMemo(() => {
    return getBrandOptions('policyLimitBIPD', state); // no 500 CSL as of 5/18/2023
  }, [state]);
  const yearsOptions = useMemo(() => {
    return getBrandOptions('numberOfPriorYearsCoverage', state);
  }, [state]);
  const [shouldShow, setShouldShow] = useState(false);

  const autoErrors = useRef(null);
  // Track previous radio button state to detect No→Yes transitions
  const prevHasAutoInsuranceDetails = useRef(values.hasAutoInsuranceDetails);

  const alerts = getAlerts(autoDetailsCodes, values.policyType);

  useEffect(() => {
    if (alerts.length && autoErrors.current && isSubmitting) {
      autoErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (sectionsToShow && sectionsToShow?.length > 0) {
      const shouldBeShown = autoDetailsCodes.every((element) => sectionsToShow.includes(element));
      setShouldShow(shouldBeShown);
    }
  }, [sectionsToShow]);

  // Determine if radio buttons should be shown (only when Auto is NOT in policy type)
  // Policy types containing 'A': 'A', 'HA', 'CA', 'AR', etc.
  const policyTypeIncludesAuto = values.policyType && (values.policyType === 'A' || values.policyType.includes('A'));
  const shouldShowToggle = !policyTypeIncludesAuto;

  // Handle radio button state changes - set default values when "No" is selected
  useEffect(() => {
    if (shouldShowToggle && values.hasAutoInsuranceDetails === false) {
      // "No" is selected - set default values
      setFieldValue('policyLimitBIPD', '25/50/25', false);
      setFieldValue('priorCoverageInYears', '0', false); // '0' = "< 1 year"
      setFieldValue('insuranceInPast31Days', false, false);
    }
  }, [values.hasAutoInsuranceDetails, shouldShowToggle, setFieldValue]);

  // Handle radio button flip from No to Yes - reset to defaults
  useEffect(() => {
    if (shouldShowToggle && values.hasAutoInsuranceDetails === true && prevHasAutoInsuranceDetails.current === false) {
      // User just flipped from No to Yes - reset to defaults
      setFieldValue('policyLimitBIPD', '', false);
      setFieldValue('priorCoverageInYears', '', false);
      setFieldValue('insuranceInPast31Days', false, false);
    }
    prevHasAutoInsuranceDetails.current = values.hasAutoInsuranceDetails;
  }, [values.hasAutoInsuranceDetails, shouldShowToggle, setFieldValue]);

  // Don't render auto details at all for Farm policies
  if (isFarmBasedPolicyType(values.policyType)) {
    return null;
  }

  return (
    <Form>
      {alerts.length > 0 && <ErrorAlert errors={alerts} ref={autoErrors} id="error-alert-auto-details" />}
      {shouldShow && (
        <Collapsible title="Current Auto Details" forceOpen={alerts.length > 0} initialOpen={alerts.length > 0}>
          <Grid container spacing={3}>
            {/* Radio button field - only show when Auto is NOT in policy type */}
            {shouldShowToggle && (
              <Grid item xs={12}>
                <div className={classes.radioLabel}>Do you know the applicant's current auto insurance details?</div>
                <Field
                  type="radio"
                  id="hasAutoInsuranceDetails"
                  name="hasAutoInsuranceDetails"
                  options={autoInsuranceDetailsOptions}
                  mode="light"
                  className={classes.radioGroup}
                  radioClassName={classes.radio}
                />
              </Grid>
            )}

            {/* Existing fields - conditionally hidden when "No" is selected */}
            {(!shouldShowToggle || values.hasAutoInsuranceDetails === true) && (
              <>
                <Grid item xs={6}>
                  <Field
                    type="select"
                    id="policyLimitBIPD"
                    name="policyLimitBIPD"
                    options={limitOptions}
                    disabled={!state}
                    fast={false}
                    mode="light"
                    label="Auto Liability Limit"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    type="select"
                    id="priorCoverageInYears"
                    name="priorCoverageInYears"
                    options={yearsOptions}
                    disabled={!state}
                    fast={false}
                    mode="light"
                    label="Years of continuous prior insurance"
                  />
                </Grid>
                <Grid item xs={8} className={classes.checkboxContainer}>
                  <Field
                    type="checkbox"
                    id="insuranceInPast31Days"
                    name="insuranceInPast31Days"
                    mode="light"
                    label="Have you had auto coverage in the last 31 days?"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Collapsible>
      )}
    </Form>
  );
};

AutoDetails.propTypes = {
  sectionsToShow: PropTypes.array.isRequired
};

export default withDatePicker(AutoDetails);
